import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { APP_CONFIG } from "../../../core/config/app-config";
import {
    ManagedWorkflow,
    WorkflowStep,
    WorkflowStepDraft,
    WorkflowStepType,
    workflowStepCode,
} from "../models/workflow.models";

interface WorkflowResponse {
    gmvid?: string;
    objectId?: string;
    company?: string;
    title?: string;
    team?: string;
    steps?: string[];
    owner?: string | null;
    ownerInfo?: ManagedWorkflow["ownerInfo"];
}

interface WorkflowStepResponse {
    _id?: string;
    gmvid?: string;
    objectId?: string;
    company?: string;
    team?: string;
    title?: string;
    shortInstruction?: string;
    longInstruction?: string;
    pictureURL?: string;
    codeToScan?: string;
    stepSort?: string;
}

interface ImageUploadResponse {
    imageKey: string;
}

@Injectable({ providedIn: "root" })
export class WorkflowsApiService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly workflowUrl = `${this.config.apiBaseUrl}/servworkflow`;

    loadWorkflows(): Observable<ManagedWorkflow[]> {
        return this.http
            .get<WorkflowResponse[]>(`${this.workflowUrl}/workflow`)
            .pipe(
                map((workflows) =>
                    workflows
                        .map((workflow) => this.mapWorkflow(workflow))
                        .sort((a, b) => a.title.localeCompare(b.title))
                )
            );
    }

    loadStep(stepId: string): Observable<WorkflowStep> {
        return this.http
            .get<WorkflowStepResponse>(
                `${this.workflowUrl}/task/${encodeURIComponent(stepId)}`
            )
            .pipe(map((step) => this.mapStep(step, stepId)));
    }

    createWorkflow(title: string): Observable<ManagedWorkflow> {
        return this.http
            .post<WorkflowResponse>(`${this.workflowUrl}/workflow/send`, {
                title,
                team: "",
                steps: [],
            })
            .pipe(map((workflow) => this.mapWorkflow(workflow)));
    }

    updateWorkflow(workflow: ManagedWorkflow): Observable<ManagedWorkflow> {
        return this.http
            .put<WorkflowResponse>(
                `${this.workflowUrl}/workflow/update/${encodeURIComponent(workflow.gmvid)}`,
                workflow
            )
            .pipe(
                map((response) =>
                    this.mapWorkflow({ ...workflow, ...response })
                )
            );
    }

    deleteWorkflow(workflowId: string): Observable<unknown> {
        return this.http.delete(
            `${this.workflowUrl}/workflow/delete/${encodeURIComponent(workflowId)}`
        );
    }

    createStep(
        workflow: ManagedWorkflow,
        draft: WorkflowStepDraft,
        pictureURL: string
    ): Observable<WorkflowStep> {
        return this.http
            .post<WorkflowStepResponse>(`${this.workflowUrl}/task/send`, {
                company: workflow.company,
                team: "",
                title: draft.shortInstruction,
                shortInstruction: draft.shortInstruction,
                longInstruction: draft.longInstruction,
                pictureURL,
                codeToScan: workflowStepCode(draft),
                stepSort: draft.stepSort,
            })
            .pipe(map((step) => this.mapStep(step, step.gmvid ?? step._id)));
    }

    updateStep(
        step: WorkflowStep,
        draft: WorkflowStepDraft,
        pictureURL: string
    ): Observable<WorkflowStep> {
        const updated: WorkflowStep = {
            ...step,
            title: draft.shortInstruction,
            shortInstruction: draft.shortInstruction,
            longInstruction: draft.longInstruction,
            pictureURL,
            codeToScan: workflowStepCode(draft),
            stepSort: draft.stepSort,
        };
        return this.http
            .put<WorkflowStepResponse>(
                `${this.workflowUrl}/task/update/${encodeURIComponent(step.gmvid)}`,
                updated
            )
            .pipe(
                map((response) =>
                    this.mapStep({ ...updated, ...response }, step.gmvid)
                )
            );
    }

    uploadWorkflowMedia(file: File, workflowId: string): Observable<string> {
        const extension =
            file.name.split(".").pop()?.toLocaleLowerCase() ?? "bin";
        const filename = `${Date.now()}_${workflowId}.${extension}`;
        const headers = new HttpHeaders({ "x-filename": filename });
        const body = new FormData();
        body.append("file", file, filename);
        return this.http
            .post<ImageUploadResponse>(
                `${this.config.apiBaseUrl}/servimages/upload/workflow`,
                body,
                { headers }
            )
            .pipe(map((response) => response.imageKey));
    }

    private mapWorkflow(response: WorkflowResponse): ManagedWorkflow {
        const id = response.gmvid || response.objectId || "";
        return {
            gmvid: id,
            objectId: response.objectId || id,
            company: response.company ?? "",
            title: response.title?.trim() || id,
            team: response.team ?? "",
            steps: response.steps ?? [],
            owner: response.owner ?? null,
            ownerInfo: response.ownerInfo ?? null,
        };
    }

    private mapStep(
        response: WorkflowStepResponse,
        originalId = ""
    ): WorkflowStep {
        const id =
            response.gmvid || response._id || response.objectId || originalId;
        return {
            gmvid: id,
            objectId: response.objectId || id,
            idUsedWhenFetching: originalId || id,
            company: response.company ?? "",
            team: response.team ?? "",
            title: response.title ?? response.shortInstruction ?? "",
            shortInstruction: response.shortInstruction ?? response.title ?? "",
            longInstruction: response.longInstruction ?? "",
            pictureURL: response.pictureURL ?? "",
            codeToScan: response.codeToScan ?? "",
            stepSort: (response.stepSort ?? "INSTRUCTIONS") as WorkflowStepType,
        };
    }
}
